/**
 * A SET of diagram parts (round 6, owner: "I want to select any group of
 * nodes and edges in the diagram and ask for more/less detail on them"), and
 * since DECISIONS #95 what rides the send when the reader asks about them in
 * their own words: the picked parts are chips in the composer, the question
 * is theirs, and `focusFor` is the structured half beside it.
 *
 * Everything here is pure text and arrays. The selection is a list of
 * LABELS, because a label is the only name the server, the mermaid source
 * and the drawn SVG all agree on (partHotspots explains why), so the same
 * value is the join key, the visible chip and the word inside the question.
 *
 * The `focus` block is the payload a region-preserving redraw needs: the
 * mermaid the model last drew plus the labels picked. Without it the model
 * rewrites the whole picture from scratch every time and "keep everything
 * else exactly as it is" is a request it can only honour by memory.
 */

import type { DiagramRegionFocus } from '../../api/sse';
import type { DiagramPart, SessionDiagram } from '../chat/transcript';

/**
 * What rides the send so the server can answer about (or redraw) ONLY the
 * selected region. The wire shape lives at the send seam (api/sse.ts); this
 * is its name here, kept because the selection is the only thing that
 * builds one.
 */
export type { DiagramRegionFocus };

/**
 * The picked labels in the DRAWING's order, deduplicated, with anything that
 * is not a part of this diagram dropped.
 *
 * Order matters because it is read twice: as the row of chips under the
 * picture and as the list inside the question. Click order would make the same
 * three boxes produce three different sentences depending on which one the
 * reader happened to press first; the diagram's own order is the order they
 * are read in on screen.
 */
export function orderedSelection(
  parts: readonly DiagramPart[],
  labels: readonly string[],
): string[] {
  const wanted = new Set(labels);
  return parts.filter((p) => wanted.has(p.label)).map((p) => p.label);
}

/** Add a label if it is missing, drop it if it is there. */
export function toggleSelection(current: readonly string[], label: string): string[] {
  return current.includes(label) ? current.filter((l) => l !== label) : [...current, label];
}

/**
 * The structured half of a question about picked parts (DECISIONS #95): the
 * drawing they were picked from, verbatim, plus the labels in the drawing's
 * order. No direction: the reader's own words say what to do with them, and
 * the server's focus block lets the question decide between a redraw and an
 * answer in words. Null when nothing of this drawing is picked.
 */
export function focusFor(
  diagram: SessionDiagram,
  labels: readonly string[],
): DiagramRegionFocus | null {
  const picked = orderedSelection(diagram.parts, labels);
  if (picked.length === 0) return null;
  return {
    kind: 'diagram_region',
    diagram_id: diagram.diagram_id,
    mermaid: diagram.mermaid,
    labels: picked,
  };
}

/** "3 parts selected", and the singular that stops it reading as a bug. */
export function selectionCountLabel(count: number): string {
  return count === 1 ? '1 part selected' : `${count} parts selected`;
}
